import { type FunctionComponent, useEffect } from 'react';

import { Button } from '@dr.pogodin/react-utils';

import { LaunchExperienceButton, usePlayback } from 'components/ai-app';

import type { UseExperienceResT } from 'hooks';

import buttonTheme from 'themes/Button/ai-app/red-h52.scss';

import './style.scss';

type PropsT = {
  editMode: boolean;
  experience: UseExperienceResT;
  green?: boolean;
  setEditMode: (editMode: boolean) => void;
};

export const EndButton: FunctionComponent<PropsT> = ({
  editMode,
  experience,
  green,
  setEditMode,
}) => {
  const { prepare, prepareStatus, unplayable } = usePlayback();

  const checkInId = experience.data?.checkInId;
  const experienceData = experience.data?.experience;

  // Prefetch sources + load segment 0 into the shared <video> as soon as the
  // experience is ready, so the Start tap can unlock it with no async gap.
  const videos = experienceData?.videos;
  useEffect(() => {
    if (videos?.length) void prepare(videos);
  }, [videos, prepare]);

  if (experienceData?.videos.length) {
    // 'blocked' = segment 0 itself has no source, so Start cannot work.
    // A non-zero `unplayable` while NOT blocked means only later clips
    // won't play — Start still works (the Session handles those per segment).
    const blocked = prepareStatus === 'blocked';
    const someUnplayable = !blocked && unplayable.length > 0;
    return (
      <div styleName={green ? 'container compact' : 'container'}>
        {blocked ? (
          <p styleName="hint">
            The first clip isn&apos;t available to play right now. Replace it to
            start your session.
          </p>
        ) : null}
        {someUnplayable ? (
          <p styleName="hint">
            {`${unplayable.length} clip(s) in this experience may not play.`}
          </p>
        ) : null}
        <LaunchExperienceButton
          checkInId={checkInId ?? null}
          experience={experienceData}
          sessionKind={experience.data?.sessionKind ?? null}
          setExperience={(e) => {
            experience.set({
              checkInId: checkInId ?? null,
              experience: e,
              sessionKind: experience.data?.sessionKind ?? null,
            });
          }}
          title="Start Your Session"
        />
      </div>
    );
  }

  if (!editMode) {
    return (
      <div styleName="container">
        <Button
          onClick={() => {
            setEditMode(true);
          }}
          theme={buttonTheme}
        >
          Edit this experience
        </Button>
      </div>
    );
  }

  return null;
};
